import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { Trans } from "react-i18next";
import React from "react";
import { Box } from "@mui/material";
import useCloseAuditDialgHooks from "./CloseAuditDialog.hooks";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { LegalEntityAuditClosedReason } from "./CloseAuditDialog.types";
import { getEnumValues } from "../../../common/enumOperations";
import DotLegalDocumentUploadWrapper from "@common/components/DotLegalDocumentUploadWrapper.tsx";
import HighlightedName from "@common/components/HighlightedName";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

export interface ICloseAuditDialog {
    onDialogClose: () => void;
    onAuditClosed: () => void;
    auditId: string;
    questionnaireName: string;
    deadline: Date;
    hasContact: boolean;
}

function CloseAuditDialog(props: ICloseAuditDialog) {
    const { translateString, translateDate } = useTranslation();
    const {
        saveModel,
        onStatusChange,
        onSendNotificationToContactChange,
        validation,
        onCloseClick,
        isSaving,
        documentUploadModel,
        setDocumentUploadModel,
        isUploading,
        uploadPercentage,
    } = useCloseAuditDialgHooks(props);

    validation.check(saveModel);

    return (
        <DotLegalDialog
            buttonOkText={translateString(saveModel.sendNotificationToContact ? "closeAndSend" : "close")}
            header={translateString("closeAudit")}
            size="sm"
            open
            onDialogClose={props.onDialogClose}
            onOkClick={() => onCloseClick()}
            inProgress={isSaving}
            disableBackdropOnCloseIfValueChange={saveModel}
            okButtonDisabled={saveModel.closedReason === undefined}
        >
            <DotLegalHeader fontWeight={400} center headerStyle="small">
                <Trans
                    i18nKey={"closeDialogContent"}
                    values={{
                        name: props.questionnaireName.trim(),
                        deadline: translateDate(props.deadline),
                    }}
                    components={{
                        wrapper: <HighlightedName />,
                    }}
                />
            </DotLegalHeader>
            <DotLegalSelect
                options={getEnumValues(LegalEntityAuditClosedReason).map((x) => {
                    const name = translateString(setFirstLetterToLowerCase(LegalEntityAuditClosedReason[x].toString()));
                    return { name: name, id: x.toString() };
                })}
                onChange={(value) => onStatusChange(Number(value))}
                label={translateString("reason")}
                noOptionsLabel=""
                placeholder={translateString("reason")}
                selectedItem={saveModel.closedReason !== undefined ? saveModel.closedReason.toString() : ""}
                disableClearable
                disableSearch
                errorText={validation.getErrorsForDisplay("closedReason")}
            />
            {props.hasContact && (
                <Box sx={{ marginLeft: "-12px" }}>
                    <DotLegalCheckbox
                        label={translateString("sendNotificationToContact")}
                        tooltip={translateString("sendNotificationToContactTooltip")}
                        checked={saveModel.sendNotificationToContact}
                        onChange={onSendNotificationToContactChange}
                    />
                </Box>
            )}
            {saveModel.closedReason === LegalEntityAuditClosedReason.AuditOpinion && (
                <Box marginTop={2}>
                    <DotLegalDocumentUploadWrapper
                        showError={validation.showErrors}
                        viewModel={documentUploadModel}
                        onDataChange={(model) => {
                            setDocumentUploadModel(model);
                        }}
                    />
                </Box>
            )}
            <UploadProgressBar isUploading={isUploading} uploadPercentage={uploadPercentage} />
        </DotLegalDialog>
    );
}

export default CloseAuditDialog;
